#!/usr/bin/env node
/**
 * ClawRouter CLI
 *
 * Standalone proxy for deployed setups where the proxy needs to survive gateway restarts.
 *
 * Usage:
 *   npx @blockrun/clawrouter              # Start standalone proxy
 *   npx @blockrun/clawrouter --version    # Show version
 *   npx @blockrun/clawrouter --port 8402  # Custom port
 *
 * For production deployments, use with PM2:
 *   pm2 start "npx @blockrun/clawrouter" --name clawrouter
 */

import { startProxy, getProxyPort } from "./proxy.js";
import { VERSION } from "./version.js";
import {
  resolveOrGenerateWalletKey,
  resolvePaymentChain,
  recoverWalletFromMnemonic,
  savePaymentChain,
} from "./auth.js";
import { getSolanaAddress } from "./wallet.js";
import { generateReport } from "./report.js";
import { formatRecentLogs } from "./stats.js";
import { runDoctor } from "./doctor.js";
import { PARTNER_SERVICES } from "./partners/index.js";

function printHelp(): void {
  console.log(`
ClawRouter v${VERSION} - Smart LLM Router

Usage:
  clawrouter [options]
  clawrouter status                    # Live proxy status (wallet, balance, chain)
  clawrouter wallet                    # Wallet details + balance
  clawrouter models                    # List available models
  clawrouter stats [--days <n>]        # Usage stats (default: 7 days)
  clawrouter doctor [opus] [question]
  clawrouter partners [test]
  clawrouter report [daily|weekly|monthly] [--json]
  clawrouter logs [--days <n>]

Options:
  --version, -v     Show version number
  --help, -h        Show this help message
  --port <number>   Port to listen on (default: ${getProxyPort()})

Query Commands (talk to running proxy on localhost:${getProxyPort()}):
  status            Proxy status: wallet, balance, payment chain
  wallet            Wallet addresses + USDC balance
  models            List all available models with pricing
  stats             Usage breakdown: requests, cost, top models
  stats --days 14   Custom time range (max 30 days)
  cache             Response cache stats (hit rate, size)

Management Commands:
  doctor            AI-powered diagnostics (default: Sonnet ~$0.003)
  doctor opus       Use Opus for deeper analysis (~$0.01)
  logs              Per-request breakdown: model, cost, latency, status
  logs --days 7     Show last 7 days of requests (default: 1 day)
  partners          List available partner APIs with pricing
  partners test     Test partner API endpoints (expect 402 = alive)
  wallet recover    Restore wallet.key from mnemonic (if generated by ClawRouter)
  chain solana      Switch to Solana (persists)
  chain base        Switch to Base EVM (persists)

Environment Variables:
  BLOCKRUN_WALLET_KEY     Private key for x402 payments (auto-generated if not set)
  BLOCKRUN_PROXY_PORT     Default proxy port (default: 8402)

For more info: https://github.com/BlockRunAI/ClawRouter
`);
}

/** Query the running proxy HTTP API */
async function queryProxy(path: string, port: number): Promise<unknown> {
  const res = await fetch(`http://127.0.0.1:${port}${path}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  return res.json();
}

async function cmdStatus(port: number): Promise<void> {
  try {
    const data = (await queryProxy("/health?full=true", port)) as Record<string, unknown>;
    console.log(`\nClawRouter Status (port ${port})\n`);
    console.log(`  Status:        ${data.status}`);
    console.log(`  Payment Chain: ${data.paymentChain}`);
    console.log(`  EVM Wallet:    ${data.wallet}`);
    if (data.solana) console.log(`  Solana Wallet: ${data.solana}`);
    console.log(`  Balance:       ${data.balance ?? "unknown"}`);
    if (data.isLow) console.log(`  ⚠ Balance is low`);
    if (data.isEmpty) console.log(`  ⚠ Balance is empty — using free models`);
    if (data.upstreamProxy) console.log(`  Upstream Proxy: ${data.upstreamProxy}`);
    console.log();
  } catch {
    console.error(`✗ Cannot connect to ClawRouter on port ${port}`);
    console.error(`  Is the proxy running? Start with: npx @blockrun/clawrouter`);
    process.exit(1);
  }
}

async function cmdWallet(port: number): Promise<void> {
  try {
    const data = (await queryProxy("/health?full=true", port)) as Record<string, unknown>;
    console.log(`\nClawRouter Wallet\n`);
    console.log(`  Payment Chain: ${data.paymentChain}`);
    console.log();
    console.log(`  Base (EVM):`);
    console.log(`    Address: ${data.wallet}`);
    console.log(`    Fund:    https://basescan.org/address/${data.wallet}`);
    if (data.solana) {
      console.log();
      console.log(`  Solana:`);
      console.log(`    Address: ${data.solana}`);
      console.log(`    Fund:    https://solscan.io/account/${data.solana}`);
    }
    console.log();
    console.log(`  Balance: ${data.balance ?? "unknown"} (USDC)`);
    if (data.isEmpty) console.log(`  ⚠ Empty — fund wallet or use free models`);
    console.log();
  } catch {
    console.error(`✗ Cannot connect to ClawRouter on port ${port}`);
    process.exit(1);
  }
}

async function cmdModels(port: number): Promise<void> {
  try {
    const data = (await queryProxy("/v1/models", port)) as {
      data: Array<{ id: string; owned_by?: string }>;
    };
    console.log(`\nAvailable Models (${data.data.length})\n`);

    // Group by provider
    const groups = new Map<string, string[]>();
    for (const m of data.data) {
      const provider = m.id.includes("/") ? m.id.split("/")[0] : "blockrun";
      if (!groups.has(provider)) groups.set(provider, []);
      groups.get(provider)!.push(m.id);
    }

    for (const [provider, models] of groups) {
      console.log(`  ${provider} (${models.length}):`);
      for (const id of models) {
        console.log(`    ${id}`);
      }
      console.log();
    }
  } catch {
    console.error(`✗ Cannot connect to ClawRouter on port ${port}`);
    process.exit(1);
  }
}

async function cmdStats(port: number, days: number): Promise<void> {
  try {
    const data = (await queryProxy(`/stats?days=${days}`, port)) as Record<string, unknown>;
    const stats = data as {
      totalRequests?: number;
      totalCostUsd?: number;
      savedUsd?: number;
      savingsPercent?: number;
      topModels?: Array<{ model: string; requests: number; costUsd: number }>;
    };

    console.log(`\nUsage Stats (last ${days} days)\n`);
    console.log(`  Requests: ${stats.totalRequests ?? 0}`);
    console.log(`  Cost:     $${(stats.totalCostUsd ?? 0).toFixed(4)}`);
    if (stats.savedUsd) {
      console.log(
        `  Saved:    $${stats.savedUsd.toFixed(4)} (${stats.savingsPercent?.toFixed(0) ?? 0}% vs Opus)`,
      );
    }

    if (stats.topModels && stats.topModels.length > 0) {
      console.log();
      console.log(`  Top Models:`);
      for (const m of stats.topModels.slice(0, 10)) {
        console.log(
          `    ${m.model.padEnd(40)} ${String(m.requests).padStart(5)} reqs  $${m.costUsd.toFixed(4)}`,
        );
      }
    }
    console.log();
  } catch {
    console.error(`✗ Cannot connect to ClawRouter on port ${port}`);
    process.exit(1);
  }
}

async function cmdCache(port: number): Promise<void> {
  try {
    const data = (await queryProxy("/cache", port)) as Record<string, unknown>;
    console.log(`\nCache Stats\n`);
    for (const [key, value] of Object.entries(data)) {
      console.log(`  ${key}: ${JSON.stringify(value)}`);
    }
    console.log();
  } catch {
    console.error(`✗ Cannot connect to ClawRouter on port ${port}`);
    process.exit(1);
  }
}

function parseArgs(args: string[]): {
  version: boolean;
  help: boolean;
  doctor: boolean;
  logs: boolean;
  logsDays: number;
  partners: boolean;
  partnersTest: boolean;
  report: boolean;
  reportPeriod: "daily" | "weekly" | "monthly";
  reportJson: boolean;
  walletRecover: boolean;
  chain?: "solana" | "base";
  port?: number;
  // Query commands
  queryStatus: boolean;
  queryWallet: boolean;
  queryModels: boolean;
  queryStats: boolean;
  queryStatsDays: number;
  queryCache: boolean;
} {
  const result = {
    version: false,
    help: false,
    doctor: false,
    logs: false,
    logsDays: 1,
    partners: false,
    partnersTest: false,
    report: false,
    reportPeriod: "daily" as "daily" | "weekly" | "monthly",
    reportJson: false,
    walletRecover: false,
    chain: undefined as "solana" | "base" | undefined,
    port: undefined as number | undefined,
    queryStatus: false,
    queryWallet: false,
    queryModels: false,
    queryStats: false,
    queryStatsDays: 7,
    queryCache: false,
  };

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === "--version" || arg === "-v") {
      result.version = true;
    } else if (arg === "--help" || arg === "-h") {
      result.help = true;
    } else if (arg === "status") {
      result.queryStatus = true;
    } else if (arg === "models") {
      result.queryModels = true;
    } else if (arg === "stats") {
      result.queryStats = true;
      if (args[i + 1] === "--days" && args[i + 2]) {
        result.queryStatsDays = Math.min(parseInt(args[i + 2], 10) || 7, 30);
        i += 2;
      }
    } else if (arg === "cache") {
      result.queryCache = true;
    } else if (arg === "doctor" || arg === "--doctor") {
      result.doctor = true;
    } else if (arg === "logs") {
      result.logs = true;
      if (args[i + 1] === "--days" && args[i + 2]) {
        result.logsDays = parseInt(args[i + 2], 10) || 1;
        i += 2;
      }
    } else if (arg === "partners") {
      result.partners = true;
      if (args[i + 1] === "test") {
        result.partnersTest = true;
        i++;
      }
    } else if (arg === "report") {
      result.report = true;
      const next = args[i + 1];
      if (next && ["daily", "weekly", "monthly"].includes(next)) {
        result.reportPeriod = next as "daily" | "weekly" | "monthly";
        i++;
        if (args[i + 1] === "--json") {
          result.reportJson = true;
          i++;
        }
      } else if (next === "--json") {
        result.reportJson = true;
        i++;
      }
    } else if (arg === "wallet") {
      if (args[i + 1] === "recover") {
        result.walletRecover = true;
        i++;
      } else if (args[i + 1] === "solana" || args[i + 1] === "base") {
        result.chain = args[i + 1] as "solana" | "base";
        i++;
      } else {
        result.queryWallet = true;
      }
    } else if (
      (arg === "chain" || arg === "/wallet") &&
      (args[i + 1] === "solana" || args[i + 1] === "base")
    ) {
      result.chain = args[i + 1] as "solana" | "base";
      i++;
    } else if (arg === "--port" && args[i + 1]) {
      result.port = parseInt(args[i + 1], 10);
      i++;
    }
  }

  return result;
}

async function main(): Promise<void> {
  const args = parseArgs(process.argv.slice(2));

  if (args.version) {
    console.log(VERSION);
    process.exit(0);
  }

  if (args.help) {
    printHelp();
    process.exit(0);
  }

  // Query commands — talk to running proxy
  const queryPort = args.port ?? getProxyPort();

  if (args.queryStatus) {
    await cmdStatus(queryPort);
    process.exit(0);
  }
  if (args.queryWallet) {
    await cmdWallet(queryPort);
    process.exit(0);
  }
  if (args.queryModels) {
    await cmdModels(queryPort);
    process.exit(0);
  }
  if (args.queryStats) {
    await cmdStats(queryPort, args.queryStatsDays);
    process.exit(0);
  }
  if (args.queryCache) {
    await cmdCache(queryPort);
    process.exit(0);
  }

  if (args.doctor) {
    // Parse: doctor [opus|sonnet] [question...]
    const rawArgs = process.argv.slice(2);
    const doctorIndex = rawArgs.findIndex((a) => a === "doctor" || a === "--doctor");
    const afterDoctor = rawArgs.slice(doctorIndex + 1);

    // Check if first arg is model selection
    let model: "sonnet" | "opus" = "sonnet"; // default to cheaper
    let questionArgs = afterDoctor;

    if (afterDoctor[0] === "opus") {
      model = "opus";
      questionArgs = afterDoctor.slice(1);
    } else if (afterDoctor[0] === "sonnet") {
      model = "sonnet";
      questionArgs = afterDoctor.slice(1);
    }

    const userQuestion = questionArgs.join(" ").trim() || undefined;
    await runDoctor(userQuestion, model);
    process.exit(0);
  }

  if (args.logs) {
    const output = await formatRecentLogs(args.logsDays);
    console.log(output);
    process.exit(0);
  }

  if (args.partners) {
    if (PARTNER_SERVICES.length === 0) {
      console.log("No partner APIs available.");
      process.exit(0);
    }

    console.log(`\nClawRouter Partner APIs (v${VERSION})\n`);

    for (const svc of PARTNER_SERVICES) {
      console.log(`  ${svc.name} (${svc.partner})`);
      console.log(`    ${svc.description}`);
      console.log(`    Tool:    blockrun_${svc.id}`);
      console.log(`    Method:  ${svc.method} /v1${svc.proxyPath}`);
      console.log(
        `    Pricing: ${svc.pricing.perUnit} per ${svc.pricing.unit} (min ${svc.pricing.minimum}, max ${svc.pricing.maximum})`,
      );
      console.log();
    }

    if (args.partnersTest) {
      console.log("Testing partner endpoints...\n");
      const apiBase = "https://blockrun.ai/api";
      for (const svc of PARTNER_SERVICES) {
        const url = `${apiBase}/v1${svc.proxyPath}`;
        try {
          const response = await fetch(url, { method: "GET" });
          const status = response.status;
          const ok = status === 402 ? "alive (402 = payment required)" : `status ${status}`;
          console.log(`  ${svc.id}: ${ok}`);
        } catch (err) {
          console.log(`  ${svc.id}: error - ${err instanceof Error ? err.message : String(err)}`);
        }
      }
      console.log();
    }

    process.exit(0);
  }

  if (args.walletRecover) {
    await recoverWalletFromMnemonic();
    process.exit(0);
  }

  if (args.chain) {
    await savePaymentChain(args.chain);
    console.log(`[ClawRouter] Payment chain set to: ${args.chain}`);
    console.log(`[ClawRouter] This persists across restarts.`);
    console.log(`[ClawRouter] Run: npx @blockrun/clawrouter`);
    process.exit(0);
  }

  if (args.report) {
    const report = await generateReport(args.reportPeriod, args.reportJson);
    console.log(report);
    process.exit(0);
  }

  // Resolve wallet key
  const wallet = await resolveOrGenerateWalletKey();

  if (wallet.source === "generated") {
    console.log(`[ClawRouter] Generated new wallet: ${wallet.address}`);
  } else if (wallet.source === "saved") {
    console.log(`[ClawRouter] Using saved wallet: ${wallet.address}`);
  } else if (wallet.source === "config") {
    console.log(`[ClawRouter] Using wallet from plugin config: ${wallet.address}`);
  } else {
    console.log(`[ClawRouter] Using wallet from BLOCKRUN_WALLET_KEY: ${wallet.address}`);
  }

  // Show Solana address if available
  if (wallet.solanaPrivateKeyBytes) {
    try {
      const solAddr = await getSolanaAddress(wallet.solanaPrivateKeyBytes);
      console.log(`[ClawRouter] Solana address: ${solAddr}`);
    } catch {
      // Non-fatal
    }
  }

  // Start the proxy
  const proxy = await startProxy({
    wallet,
    port: args.port,
    onReady: (port) => {
      console.log(`[ClawRouter] v${VERSION} | Proxy listening on http://127.0.0.1:${port}`);
      console.log(`[ClawRouter] Health check: http://127.0.0.1:${port}/health`);
    },
    onError: (error) => {
      console.error(`[ClawRouter] Error: ${error.message}`);
    },
    onRouted: (decision) => {
      const cost = decision.costEstimate.toFixed(4);
      const saved = (decision.savings * 100).toFixed(0);
      console.log(`[ClawRouter] [${decision.tier}] ${decision.model} $${cost} (saved ${saved}%)`);
    },
    onLowBalance: (info) => {
      console.warn(`[ClawRouter] Low balance: ${info.balanceUSD}. Fund: ${info.walletAddress}`);
    },
    onInsufficientFunds: (info) => {
      console.error(
        `[ClawRouter] Insufficient funds. Balance: ${info.balanceUSD}, Need: ${info.requiredUSD}`,
      );
      console.error(`[ClawRouter] Need help? Run: npx @blockrun/clawrouter doctor`);
    },
  });

  // Check balance on the active payment chain
  const paymentChain = await resolvePaymentChain();
  const displayAddress =
    paymentChain === "solana" && proxy.solanaAddress ? proxy.solanaAddress : wallet.address;
  try {
    const balance = await proxy.balanceMonitor.checkBalance();
    if (balance.isEmpty) {
      console.log(`[ClawRouter] Wallet balance: $0.00 (using FREE model)`);
      console.log(`[ClawRouter] Fund wallet for premium models: ${displayAddress}`);
    } else if (balance.isLow) {
      console.log(`[ClawRouter] Wallet balance: ${balance.balanceUSD} (low)`);
    } else {
      console.log(`[ClawRouter] Wallet balance: ${balance.balanceUSD}`);
    }
    // Invalidate after display so the first real request always gets a fresh check.
    // Prevents a flaky RPC at startup from caching a false $0.00 and triggering
    // free-model fallback on the first request.
    proxy.balanceMonitor.invalidate();
  } catch {
    console.log(`[ClawRouter] Wallet: ${displayAddress} (balance check pending)`);
  }

  console.log(`[ClawRouter] Ready - Ctrl+C to stop`);

  // Handle graceful shutdown
  const shutdown = async (signal: string) => {
    console.log(`\n[ClawRouter] Received ${signal}, shutting down...`);
    try {
      await proxy.close();
      console.log(`[ClawRouter] Proxy closed`);
      process.exit(0);
    } catch (err) {
      console.error(`[ClawRouter] Error during shutdown: ${err}`);
      process.exit(1);
    }
  };

  process.on("SIGINT", () => shutdown("SIGINT"));
  process.on("SIGTERM", () => shutdown("SIGTERM"));

  // Keep process alive
  await new Promise(() => {});
}

main().catch((err) => {
  console.error(`[ClawRouter] Fatal error: ${err.message}`);
  console.error(`[ClawRouter] Need help? Run: npx @blockrun/clawrouter doctor`);
  process.exit(1);
});
